/**
 * Workflow Done tab — W1-A2 / #44605.
 *
 * The prototype's Done tab was a Next server component that called
 * `loadDoneView` (a `server-only` module) and passed plain props down. The Vite
 * SPA cannot value-import `server-only` (that 500s every route in the shell), so
 * the read becomes this Operation and a Hono `mount(…, READ, …)` handler. The
 * client tab consumes it with `useApi` — never a direct import of the loader.
 *
 * Wire shapes match `excellent/src/optimization/workflow-done-types.ts` 1:1.
 * The product path sets `isFallback: false` always: a fabricated criterion is
 * worse than an empty panel (RECURSIVE-WORKFLOWS-COMPLETION-PRD W1-A2).
 */

import { z } from "zod";
import type { Operation } from "../../types";

// ── Wire shapes (mirrors workflow-done-types; packages/ui cannot import excellent/) ─

export type LoopStage =
  | "derive"
  | "evaluate"
  | "refuse"
  | "park"
  | "retry"
  | "land"
  | "promote"
  | "integrated";

export interface LoopNodeDto {
  stage: LoopStage;
  ops_kind: string | null;
  ops_id: number | null;
  at: string;
  title: string;
  ok: boolean;
  reason: string | null;
  proofs: string[];
  remedy: string | null;
  tree: string | null;
  attempt: number | null;
  ops_status: "open" | "resolved" | null;
  resolve_note: string | null;
  delta?: { fixed: string[]; still_failing: string[]; newly_failing: string[] };
}

export interface ReceiptDto {
  id: number;
  command: string;
  exit_code: number;
  tree_hash: string;
  binding: "staged" | "dirty";
  mode: "absolute" | "delta" | null;
  base_tree_hash: string | null;
  base_count: number | null;
  head_count: number | null;
  base_commit: string | null;
  delta_count: number | null;
  untracked_count: number | null;
  untracked_paths: string | null;
  started_at: string;
  recorded_at: string;
}

export interface CriterionDto {
  id: number;
  uid: string;
  kind: "mechanical" | "judged";
  ref: string;
  source: "derived" | "authored" | "learned" | "customer";
  why: string;
  bound_at: string;
  latest: ReceiptDto | null;
  /** Live pass/fail from `readTaskDod` (pass === true). */
  binds: boolean;
}

export interface VerificationDto {
  agent_id: string;
  /** approve | reject | no-verdict — reject must never paint as silence (#44394). */
  decision: "approve" | "reject" | "no-verdict";
  at: string | null;
  note: string;
  mutations: string | null;
}

export interface CommitDto {
  sha: string;
  parent_sha: string;
  subject: string;
  committed_at: string;
  rewritten_from_sha: string | null;
  link_kind: "auto_in_progress" | "message_ref" | "manual";
  linked_at: string;
}

export interface IntegrationStampDto {
  // `target-retired` (#44828): the work is on the base branch, but the tier it
  // was classified against is retired — an ancestor of the base with 0 ahead.
  state: "integrated" | "none-required" | "stranded" | "stranded-legacy" | "target-retired";
  integration_sha: string | null;
  integrated_at: string | null;
  completed_at: string;
}

export interface DependencyPeerDto {
  id: number;
  title: string;
  status: string;
  integrated_at: string | null;
  integration_sha: string | null;
  integration_state: string | null;
  verifier: string | null;
}

export interface DependencyEdgeDto {
  edge_kind: string;
  source: string | null;
  computed_at: string;
  dependent: DependencyPeerDto | null;
  blocker: DependencyPeerDto | null;
  satisfied: boolean;
  satisfied_by: "cancelled" | "integrated" | "commitless" | "canonical-pass" | null;
}

export interface DependencyGateDto {
  edges: DependencyEdgeDto[];
  /** Verbatim from `packages/excellent-mcp/src/tools/tasks.ts`. `null` when no
   *  dependency is unsatisfied — the machine emits a refusal ONLY when a claim
   *  would be refused, so a claimable task must not carry a counterfactual one. */
  refusal: { reason: string; message: string; predicate: string } | null;
}

export interface DoneTaskDto {
  id: number;
  uid: string;
  title: string;
  status: string;
  shipper: string;
  verifier: string;
  created_at: string;
  tree_under_review: string;
  criteria: CriterionDto[];
  loop: LoopNodeDto[];
  commits: CommitDto[];
  verifications: VerificationDto[];
  stamp: IntegrationStampDto;
  dependency: DependencyGateDto | null;
}

/**
 * The Done tab payload. TRI-STATE `readable` matches `TaskDodView`: a failed
 * read is not an empty loop. `isFallback` is always false on the product path —
 * the styleguide may still use its frozen deck; production invents nothing.
 */
export interface WorkflowDonePayload {
  readable: boolean;
  unreadableReason: string | null;
  task: DoneTaskDto | null;
  candidates: { id: number; title: string }[];
  deltaReceipts: ReceiptDto[];
  stampTotals: { integrated: number; noneRequired: number };
  isFallback: false;
  /** Workflow slug the request was for (echo). */
  slug: string;
  /** Whether this slug is the DoD sample loop (`code-ship-verify`). */
  isDodSourceLoop: boolean;
}

const doneParams = z.object({ slug: z.string().min(1) });
/**
 * `done` (not `task`) — `task` is claimed shell-wide by the object peek, so
 * `?task=` opens the drawer over this tab. Same hazard the prototype documents.
 */
const doneQuery = z.object({
  done: z.coerce.number().int().positive().optional(),
});

const getOp: Operation<
  { slug: string },
  { done?: number },
  void,
  WorkflowDonePayload
> = {
  method: "GET",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/done`,
  params: doneParams,
  query: doneQuery,
  response: z.custom<WorkflowDonePayload>(),
};

export const workflowDone = {
  get: getOp,
};
